import { useState } from "react";
import { MessageSquare, Send } from "lucide-react";
import { toast } from "@/components/feedback/toast";
import { ADMIN_TIME_ZONE } from "@/lib/format/admin-time";
import { adminFetch } from "@/api";

export interface FeedbackHistoryItem {
  id: string;
  actorEmail: string;
  message: string;
  statusChangedTo: string | null;
  createdAt: string;
}

export interface FeedbackHistoryProps {
  feedbackId: string;
  initial: FeedbackHistoryItem[];
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    // Pinned: the server render (UTC) and the browser must agree.
    timeZone: ADMIN_TIME_ZONE,
    timeZoneName: "short",
  });
}

export function FeedbackHistory({
  feedbackId,
  initial,
}: FeedbackHistoryProps) {
  const [items, setItems] = useState(initial);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!draft.trim()) return;
    setSaving(true);
    try {
      const res = await adminFetch(
        `/api/feedback/${feedbackId}/responses`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ message: draft }),
        },
      );
      if (!res.ok) {
        toast.error("Failed to add comment");
        return;
      }
      // Refetch instead of optimistic append so createdAt + id come
      // from the server.
      const list = await adminFetch(
        `/api/feedback/${feedbackId}/responses`,
      ).then((r) => r.json());
      setItems(list.responses ?? []);
      setDraft("");
      toast.success("Comment added");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-2xl border border-fc-border bg-gradient-to-b from-fc-raised to-fc-overlay p-5">
      <div className="mb-4 flex items-center gap-2">
        <MessageSquare className="w-4 h-4 text-fc-text-dim" />
        <div>
          <div className="text-[13px] font-semibold text-fc-text">
            History &amp; comments
          </div>
          <div className="text-[11.5px] text-fc-text-dim">
            {items.length === 0
              ? "No admin activity yet"
              : `${items.length} entr${items.length === 1 ? "y" : "ies"}`}
          </div>
        </div>
      </div>

      {items.length > 0 && (
        <ul className="mb-4 flex flex-col gap-3">
          {items.map((r) => (
            <li
              key={r.id}
              className="rounded-xl border border-fc-border bg-fc-surface p-3"
            >
              <div className="flex items-center justify-between text-[11px] text-fc-text-dim mb-1">
                <span className="font-medium text-fc-text-secondary">
                  {r.actorEmail}
                </span>
                <span className="font-mono">{formatDateTime(r.createdAt)}</span>
              </div>
              {r.statusChangedTo && (
                <div className="mb-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-fc-brand/10 border border-fc-brand/20 text-fc-brand text-[10px] font-semibold uppercase tracking-wider">
                  → {r.statusChangedTo}
                </div>
              )}
              <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-fc-text">
                {r.message}
              </p>
            </li>
          ))}
        </ul>
      )}

      <div className="space-y-2">
        <label className="block text-[10.5px] uppercase tracking-wider font-semibold text-fc-text-dim">
          Add comment
        </label>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={3}
          maxLength={2000}
          placeholder="Ask a follow-up, note internal context, or record what was done"
          className="w-full px-3 py-2 rounded-xl bg-fc-surface border border-fc-border text-[13px] text-fc-text placeholder:text-fc-text-faint focus:outline-none focus:border-fc-brand resize-none"
        />
        <div className="flex justify-end">
          <button
            onClick={submit}
            disabled={saving || !draft.trim()}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-fc-brand text-black text-[12.5px] font-semibold hover:brightness-105 disabled:opacity-40 transition-all"
          >
            <Send className="w-3.5 h-3.5" />
            {saving ? "Sending…" : "Add comment"}
          </button>
        </div>
      </div>
    </section>
  );
}
